import React from "react";
import { Box, Paper, Stack, Typography, Chip } from "@mui/material";
import trustBudMark from "../../assets/logo/trustbud-mark.png";

function AuthShell({
  background,
  heroBackground,
  formBackground,
  heroTone = "dark",
  heroBadge,
  heroTitle,
  heroBody,
  heroBullets = [],
  heroNote,
  eyebrow,
  title,
  body,
  children,
  maxWidth = 980,
  reverseOnDesktop = false,
}) {
  const darkHero = heroTone === "dark";
  const heroTextColor = darkHero ? "white" : "#1a2433";
  const heroBodyColor = darkHero ? "rgba(255,255,255,0.78)" : "#5f6978";
  const heroMutedColor = darkHero ? "rgba(255,255,255,0.72)" : "#455466";
  const logoBoxStyles = darkHero
    ? {
        backgroundColor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(12px)",
      }
    : {
        backgroundColor: "rgba(26, 52, 90, 0.08)",
      };
  const noteBoxStyles = darkHero
    ? {
        backgroundColor: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.12)",
      }
    : {
        backgroundColor: "rgba(255,255,255,0.72)",
        border: "1px solid rgba(208, 218, 230, 0.8)",
      };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 2, sm: 4 },
        background,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth,
          overflow: "hidden",
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: reverseOnDesktop ? "1.02fr 0.98fr" : "0.98fr 1.02fr" },
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.12)",
          border: "1px solid rgba(215, 226, 238, 0.9)",
          bgcolor: "#ffffff",
        }}
      >
        <Box
          sx={{
            order: { xs: 1, md: reverseOnDesktop ? 2 : 1 },
            p: { xs: 3, sm: 4, md: 5 },
            background: heroBackground,
            color: heroTextColor,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: { md: 640 },
            position: "relative",
            overflow: "hidden",
            "&::after": darkHero
              ? {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle at 18% 12%, rgba(143,210,193,0.15), transparent 32%), radial-gradient(circle at 88% 22%, rgba(90,130,186,0.18), transparent 34%)",
                  pointerEvents: "none",
                }
              : {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle at 18% 12%, rgba(46,139,118,0.08), transparent 32%)",
                  pointerEvents: "none",
                },
            "& > *": {
              position: "relative",
              zIndex: 1,
            },
          }}
        >
          <Stack spacing={3}>
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...logoBoxStyles,
              }}
            >
              <img
                src={trustBudMark}
                alt="TrustBud logo"
                style={{ width: 42, height: 42 }}
              />
            </Box>

            <Stack spacing={1.5}>
              {heroBadge ? (
                <Chip
                  label={heroBadge}
                  sx={{
                    alignSelf: "flex-start",
                    color: darkHero ? "white" : "#583d1e",
                    backgroundColor: darkHero ? "rgba(255,255,255,0.14)" : "#ead7bb",
                    borderRadius: "8px",
                    fontWeight: 700,
                  }}
                />
              ) : null}
              <Typography
                variant="h3"
                sx={{
                  color: heroTextColor,
                  fontWeight: 850,
                  lineHeight: 1.05,
                  letterSpacing: 0,
                  textShadow: darkHero ? "0 1px 18px rgba(0,0,0,0.28)" : "none",
                }}
              >
                {heroTitle}
              </Typography>
              <Typography variant="body1" sx={{ color: heroBodyColor, maxWidth: 440 }}>
                {heroBody}
              </Typography>
            </Stack>

            {heroBullets.length ? (
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                {heroBullets.map((item) => (
                  <Box key={item} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: darkHero ? 8 : 9,
                        height: darkHero ? 8 : 9,
                        borderRadius: "50%",
                        mt: 0.8,
                        backgroundColor: darkHero ? "#8fd2c1" : "#2e8b76",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: darkHero ? "rgba(255,255,255,0.76)" : "#526071" }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : null}
          </Stack>

          {heroNote ? (
            <Box
              sx={{
                mt: { xs: 4, md: 8 },
                p: darkHero ? 2 : 2.25,
                borderRadius: "8px",
                ...noteBoxStyles,
              }}
            >
              <Typography variant="body2" sx={{ color: heroMutedColor }}>
                {heroNote}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            order: { xs: 2, md: reverseOnDesktop ? 1 : 2 },
            p: { xs: 3, sm: 4, md: 5 },
            background: formBackground,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Stack spacing={3} sx={{ width: "100%" }}>
            <Box>
              {eyebrow ? (
                <Typography variant="overline" sx={{ color: "#6a7483", letterSpacing: 1.4 }}>
                  {eyebrow}
                </Typography>
              ) : null}
              <Typography variant="h4" sx={{ fontWeight: 850, color: "#102033", mt: eyebrow ? 0.5 : 0, letterSpacing: 0 }}>
                {title}
              </Typography>
              {body ? (
                <Typography variant="body1" sx={{ color: "#5f6978", mt: 1 }}>
                  {body}
                </Typography>
              ) : null}
            </Box>

            {children}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default AuthShell;
